import { Box, Chip, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import CustomToolTip from "../../../../components/CustomTooltip";
import FilterMenu from "../../../../components/filters/FilterMenu";
import SearchInput from "../../../../components/filters/SearchInput";
import LoadingIcon from "../../../../components/LoadingIcon";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { Toast } from "../../../../components/Toast";
import { searchObjectProperties } from "../../../../utils/search";
import AWSDrawer from "./AWSDrawer";

const AWSManageAccounts = ({ awsRegionOptions, guideMode = false }) => {
  const [loading, setLoading] = useState(true);
  const [integratedAccounts, setIntegratedAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
    organization: {},
  });
  const [sortModel, setSortModel] = useState([
    { field: "nickname", sort: "asc" },
  ]);
  const [filteredRows, setFilteredRows] = useState([]);
  const { updateAWSAccount, fetchAllAWSAccounts, deleteAWSAccount } = useAPI();
  const [filterOptions, setFilterOptions] = useState({
    organization: [],
  });

  const accountOnClick = (account) => {
    setSelectedAccount(account);
  };

  const onDrawerClose = () => {
    setSelectedAccount(null);
  };

  const handleUpdateAccount = (account) => {
    const accountExists = Object.values(integratedAccounts).some(
      (a) => a.account_id === account.account_id
    );
    if (!accountExists) {
      Toast.warning("Account does not exist");
      setLoading(false);
      return;
    }
    updateAWSAccount(
      account.role_ARN,
      account.regions,
      account.nickname,
      account.account_id,
      account.organization_id ? account.organization_id : "",
      account.is_master
    )
      .then((response) => {
        fetchAllAWSAccounts().then((response) => {
          const allOrganizations = new Set();
          const updatedAccounts = response.data;
          const accountsArray = Object.entries(response.data).map(
            ([key, value]) => ({
              id: key,
              ...value,
            })
          );
          accountsArray.forEach((account) => {
            if (account.organization) {
              allOrganizations.add(account.organization);
            }
          });
          setFilterOptions({
            organization: Array.from(allOrganizations),
          });
          setIntegratedAccounts(accountsArray);
          let newSelectedAccount = Object.values(updatedAccounts).find(
            (a) => a.account_id === selectedAccount.account_id
          );
          setSelectedAccount(newSelectedAccount);

          Toast.success("AWS account integration updated successfully");
        });
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      });
  };

  const handleDeleteAccount = (account) => {
    setLoading(true);
    const accountExists = Object.values(integratedAccounts).some(
      (a) => a.account_id === account.account_id
    );
    if (!accountExists) {
      Toast.warning("Account does not exist");
      setLoading(false);
      return;
    }
    deleteAWSAccount(account.account_id)
      .then((response) => {
        fetchAllAWSAccounts().then((response) => {
          setLoading(false);
          setSelectedAccount(null);

          const accountsArray = Object.entries(response.data).map(
            ([key, value]) => ({
              id: key,
              ...value,
            })
          );
          setIntegratedAccounts(accountsArray);
          Toast.success("AWS account integration deleted successfully");
        });
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateNickname = (nickname) => {
    const accountExists = Object.values(integratedAccounts).some(
      (a) => a.account_id === selectedAccount.account_id
    );
    if (!accountExists) {
      Toast.warning("Account does not exist");
      setLoading(false);
      return;
    }
    updateAWSAccount(
      selectedAccount.role_ARN,
      selectedAccount.regions,
      nickname,
      selectedAccount.account_id,
      selectedAccount.organization_id ? selectedAccount.organization_id : "",
      selectedAccount.is_master
    )
      .then((response) => {
        fetchAllAWSAccounts().then((response) => {
          const accountsArray = Object.entries(response.data).map(
            ([key, value]) => ({
              id: key,
              ...value,
            })
          );
          setIntegratedAccounts(accountsArray);
          Toast.success("AWS account nickname updated successfully");
        });
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      });
  };

  useEffect(() => {
    setLoading(true);
    fetchAllAWSAccounts()
      .then((response) => {
        const accountsArray = Object.entries(response.data).map(
          ([key, value]) => ({
            id: key,
            ...value,
          })
        );
        const allOrganizations = new Set();
        accountsArray.forEach((account) => {
          if (account.organization) {
            allOrganizations.add(account.organization);
          }
        });
        setFilterOptions({
          organization: Array.from(allOrganizations),
        });

        setIntegratedAccounts(accountsArray);
        setLoading(false);
      })
      .catch((error) => {
        Toast.error("Failed to load AWS accounts");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (integratedAccounts) {
      let rows = integratedAccounts;

      if (appliedFilters.searchString !== "") {
        const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
        rows = rows.filter((row) =>
          searchObjectProperties(row, lowerCaseSearchString)
        );
      }
      if (Object.keys(appliedFilters.organization).length > 0) {
        const organization_filter = Object.keys(
          appliedFilters.organization
        ).filter((key) => appliedFilters.organization[key] === true);
        if (organization_filter.length > 0) {
          rows = integratedAccounts.filter((account) =>
            organization_filter.includes(account.organization)
          );
        }
      }
      setFilteredRows(rows);
    }
  }, [appliedFilters, integratedAccounts]);

  if (loading) {
    return <LoadingIcon />;
  }

  const columns = [
    {
      field: "nickname",
      headerName: "Account Name",
      flex: 0.2,
      renderCell: (params) => {
        return (
          <Typography sx={{ marginLeft: "8px" }} variant="body2" noWrap>
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "account_id",
      headerName: "Account ID",
      flex: 0.4,
      renderCell: (params) => {
        return (
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "organization",
      headerName: "Organization",
      flex: 0.4,
      renderCell: (params) => {
        return (
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "regions",
      headerName: "Regions to scan",
      flex: 0.4,
      renderCell: (params) => {
        const regions = params.value || [];
        if (regions.length === 0) return null;

        // Convert the array of objects into a lookup map for quick access
        const awsRegionLookup = awsRegionOptions.reduce((acc, region) => {
          acc[region.code] = region.name;
          return acc;
        }, {});

        return (
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {regions.slice(0, 3).map((regionCode, index) => (
              <CustomToolTip
                placement="top"
                key={index}
                title={`${awsRegionLookup[regionCode] || "Unknown"} `}
              >
                <Chip label={regionCode} size="small" />
              </CustomToolTip>
            ))}
            {regions.length > 3 && (
              <CustomToolTip
                title={regions
                  .slice(3) // Only include the remaining regions
                  .map(
                    (regionCode) =>
                      `${
                        awsRegionLookup[regionCode] || "Unknown"
                      } (${regionCode})`
                  )
                  .join(", ")}
              >
                <Chip label={`+${regions.length - 3}`} size="small" />
              </CustomToolTip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box mt={2}>
      <Grid container sx={{ marginBottom: "16px" }} display={"flex"} gap={1}>
        {/* SEARCH */}
        <Grid item xs={3}>
          <SearchInput
            fieldName={"searchString"}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Grid>
        {/* Filter by organization */}
        <FilterMenu
          filterName={"Organization"}
          fieldName={"organization"}
          options={filterOptions.organization.map((org) => {
            return {
              name: org,
              label: org,
              displayName: org,
            };
          })}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          fullOptions={filterOptions.organization}
        />
      </Grid>
      <StyledDataGrid
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        rows={filteredRows}
        columns={columns}
        sx={{
          cursor: "pointer",
          "& .MuiDataGridCell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
            {
              paddingLeft: "16px",
            },
          "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
            {
              paddingRight: "16px",
            },
        }}
        onRowClick={(params) => accountOnClick(params.row)}
        pageSizeOptionsOverride={[10, 15, 20]}
      />
      <AWSDrawer
        awsRegionOptions={awsRegionOptions}
        account={selectedAccount}
        isOpen={selectedAccount !== null}
        closeDrawer={onDrawerClose}
        handleUpdateAccount={handleUpdateAccount}
        handleDeleteAccount={handleDeleteAccount}
        handleUpdateNickname={handleUpdateNickname}
        guideMode={guideMode}
      />
    </Box>
  );
};

export default AWSManageAccounts;
